// TelcoTrack — Změny oproti PD: záložky, druhy změn, metadata klíče

import {
  WORKFLOW_NOTE_STATUSES,
  normalizeWorkflowNoteStatus,
  type WorkflowNoteStatus,
} from './workflow-note-status.constants';

/** Záložky detailu stavby na `/pd-changes/:polygonId`. */
export const PD_CHANGES_TABS = {
  NOTES: 'notes',
  HP_COUNT: 'hp-count',
  NAVAZNOST: 'navaznost',
  ODBOCKY: 'odbocky',
} as const;

export type PdChangesTabId = (typeof PD_CHANGES_TABS)[keyof typeof PD_CHANGES_TABS];

export const PD_CHANGES_TAB_VALUES: readonly PdChangesTabId[] = [
  PD_CHANGES_TABS.NOTES,
  PD_CHANGES_TABS.HP_COUNT,
  PD_CHANGES_TABS.NAVAZNOST,
  PD_CHANGES_TABS.ODBOCKY,
] as const;

export const PD_CHANGES_TAB_LABELS_CS: Record<PdChangesTabId, string> = {
  [PD_CHANGES_TABS.NOTES]: 'Poznámky',
  [PD_CHANGES_TABS.HP_COUNT]: 'Počet HP',
  [PD_CHANGES_TABS.NAVAZNOST]: 'Návaznost na rozvaděč',
  [PD_CHANGES_TABS.ODBOCKY]: 'Odbočky',
};

/** Druh položky pro PATCH status (mimo workflow poznámky). */
export const PD_CHANGES_ITEM_KINDS = {
  HP_COUNT: 'hpCount',
  NAVAZNOST: 'navaznost',
  ODBOCKA: 'odbocka',
} as const;

export type PdChangesItemKind = (typeof PD_CHANGES_ITEM_KINDS)[keyof typeof PD_CHANGES_ITEM_KINDS];

export const PD_CHANGES_ITEM_KIND_VALUES: readonly PdChangesItemKind[] = [
  PD_CHANGES_ITEM_KINDS.HP_COUNT,
  PD_CHANGES_ITEM_KINDS.NAVAZNOST,
  PD_CHANGES_ITEM_KINDS.ODBOCKA,
] as const;

export function isPdChangesItemKind(value: unknown): value is PdChangesItemKind {
  return (
    typeof value === 'string' &&
    (PD_CHANGES_ITEM_KIND_VALUES as readonly string[]).includes(value)
  );
}

export function isPdChangesTabId(value: unknown): value is PdChangesTabId {
  return (
    typeof value === 'string' &&
    (PD_CHANGES_TAB_VALUES as readonly string[]).includes(value)
  );
}

/**
 * Metadata cesty:
 * `metadata.customFields.pdChangesStatuses.{hpCount|navaznost|odbocka}` = WorkflowNoteStatus
 * `metadata.customFields.pdImportedNavaznost` = { rozvadecObjectId, rozvadecName }
 */
export const PD_CHANGES_CUSTOM_FIELD_KEYS = {
  statuses: 'pdChangesStatuses',
  importedNavaznost: 'pdImportedNavaznost',
} as const;

export type PdImportedNavaznost = {
  rozvadecObjectId: string;
  rozvadecName: string;
};

export type PdChangesStatusesMap = Partial<Record<PdChangesItemKind, WorkflowNoteStatus>>;

export function readPdChangesStatuses(customFields: unknown): PdChangesStatusesMap {
  if (!customFields || typeof customFields !== 'object' || Array.isArray(customFields)) {
    return {};
  }
  const raw = (customFields as Record<string, unknown>)[PD_CHANGES_CUSTOM_FIELD_KEYS.statuses];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: PdChangesStatusesMap = {};
  for (const kind of PD_CHANGES_ITEM_KIND_VALUES) {
    if (kind in (raw as Record<string, unknown>)) {
      out[kind] = normalizeWorkflowNoteStatus((raw as Record<string, unknown>)[kind]);
    }
  }
  return out;
}

export function readPdChangesItemStatus(
  customFields: unknown,
  kind: PdChangesItemKind,
): WorkflowNoteStatus {
  const map = readPdChangesStatuses(customFields);
  return map[kind] ?? WORKFLOW_NOTE_STATUSES.OPEN;
}

export function readPdImportedNavaznost(customFields: unknown): PdImportedNavaznost | null {
  if (!customFields || typeof customFields !== 'object' || Array.isArray(customFields)) {
    return null;
  }
  const raw = (customFields as Record<string, unknown>)[
    PD_CHANGES_CUSTOM_FIELD_KEYS.importedNavaznost
  ];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o['rozvadecObjectId'] === 'string' ? o['rozvadecObjectId'].trim() : '';
  if (!id) return null;
  const name = typeof o['rozvadecName'] === 'string' ? o['rozvadecName'].trim() : '';
  return { rozvadecObjectId: id, rozvadecName: name };
}

/**
 * Pokud chybí PD baseline návaznosti a objekt má předchozí rozvaděč odlišný od nového,
 * zapíše baseline (první známá vazba = PD). Vrací true, když metadata změnil.
 */
export function ensurePdImportedNavaznostBaseline(
  customFields: Record<string, unknown>,
  previous: { rozvadecObjectId?: string | null; rozvadecName?: string | null },
  nextRozvadecObjectId: string | null | undefined,
): boolean {
  if (readPdImportedNavaznost(customFields)) return false;
  const prevId = (previous.rozvadecObjectId ?? '').trim();
  if (!prevId) return false;
  const nextId = (nextRozvadecObjectId ?? '').trim();
  if (prevId === nextId) return false;
  customFields[PD_CHANGES_CUSTOM_FIELD_KEYS.importedNavaznost] = {
    rozvadecObjectId: prevId,
    rozvadecName: (previous.rozvadecName ?? '').trim(),
  } satisfies PdImportedNavaznost;
  return true;
}
